import React, { ReactNode } from 'react';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Download, Filter, Search, RefreshCw, Plus } from 'lucide-react';

interface DataGridLayoutProps {
  title: string;
  subtitle?: string;
  description?: string;
  children: ReactNode;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  onRefresh?: () => void;
  onExport?: () => void;
  onAdd?: () => void;
  actions?: ReactNode;
  searchComponent?: ReactNode;
}

const DataGridLayout: React.FC<DataGridLayoutProps> = ({
  title,
  subtitle,
  description,
  children,
  searchPlaceholder = "Search...",
  onSearch,
  onRefresh,
  onExport,
  onAdd,
  actions,
  searchComponent
}) => {
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header title={title} subtitle={subtitle} />
      
      <div className="flex-1 overflow-auto p-6">
        <Card className="shadow-sm border-gray-200">
          <CardHeader className="pb-2">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-xl font-semibold text-gray-800">{title}</CardTitle>
                {subtitle && <CardDescription>{subtitle}</CardDescription>}
                {description && <CardDescription className="text-gray-500 mt-1">{description}</CardDescription>}
              </div>
              
              <div className="flex flex-col md:flex-row gap-2">
                {onAdd && (
                  <Button 
                    className="bg-[#0071bd] hover:bg-[#005a97]"
                    size="sm"
                    onClick={onAdd}
                  >
                    <Plus size={16} className="mr-1" />
                    Add New
                  </Button>
                )}
                {actions}
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between gap-2 mt-4">
              {searchComponent ? (
                searchComponent
              ) : (
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input 
                    placeholder={searchPlaceholder}
                    className="pl-9 h-9"
                    onChange={(e) => onSearch && onSearch(e.target.value)}
                  />
                </div>
              )}
              
              <div className="flex items-center gap-2">
                {onRefresh && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={onRefresh}
                    className="h-9"
                  >
                    <RefreshCw size={16} className="mr-1" />
                    Refresh
                  </Button>
                )}
                {onExport && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={onExport}
                    className="h-9"
                  >
                    <Download size={16} className="mr-1" />
                    Export
                  </Button>
                )}
                <Button variant="outline" size="sm" className="h-9">
                  <Filter size={16} className="mr-1" />
                  Filter
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="pt-2">
            {children}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DataGridLayout;